import { useMutation, type QueryClient } from '@tanstack/react-query';

import { type AuditFormService } from '#apps/client-web/features/audit-form/services/audit-form';
import { type FileStorageService } from '#apps/client-web/features/file-storage/services/file-storage';
import { type SyncApiService } from '#apps/client-web/features/sync/services/sync-api';
import { type UpdateSyncStateAction } from '#apps/client-web/pages/audit-details/audit-details-page.view-model';

import { type LoggingContext } from '#packages/data-collection/types/logging-context';
import {
  AuditSchema,
  type Audit,
  type AuditId,
} from '#packages/data-collection/models/audit';
import { type AuditFormType } from '#packages/data-collection/models/audit-form';
import { type AuditMechanicalForm } from '#packages/data-collection/models/audit-mechanical-form';

import { SYNC_AUDIT_QUERY_KEY } from '../types/react-query';

const ALL_FORM_TYPES: AuditFormType[] = ['mechanical'];

export type FormLoadState = 'idle' | 'loading' | 'success' | 'error';
export type FormSyncState = 'idle' | 'pending' | 'success' | 'error';

type AuditFormTypesWithAttachments = Extract<
  AuditFormType,
  'background' | 'mechanical'
>;

export type AuditSyncState = {
  preparing: FormLoadState;
  forms: Record<AuditFormType, FormSyncState>;
  attachments: Record<AuditFormTypesWithAttachments, FormSyncState>;
};

export const useSyncAuditMutation = ({
  auditId,
  queryClient,
  auditFormService,
  fileStorageService,
  syncApiService,
  getAccessToken,
  logger,
  updateState,
}: {
  auditId: AuditId;
  queryClient: QueryClient;
  auditFormService: AuditFormService;
  fileStorageService: FileStorageService;
  syncApiService: SyncApiService;
  getAccessToken: () => string | undefined;
  updateState: (action: UpdateSyncStateAction) => void;
} & LoggingContext) =>
  useMutation(
    {
      mutationKey: [SYNC_AUDIT_QUERY_KEY, auditId],
      mutationFn: async (audit: Audit) => {
        const auditId = audit.id;
        const accessToken = getAccessToken();

        // 1. Load all forms from local storage
        updateState({
          type: 'update',
          payload: {
            preparing: 'loading',
          },
        });
        logger?.debug({ auditId }, 'Loading audit forms for sync');
        console.time('load-all-forms-from-local-storage');
        const formResults = await Promise.all(
          ALL_FORM_TYPES.map((formType) =>
            auditFormService.loadByType({ auditId, formType }),
          ),
        ).catch(() => {
          updateState({
            type: 'update',
            payload: {
              preparing: 'error',
            },
          });
        });
        console.timeEnd('load-all-forms-from-local-storage');
        const forms = (formResults ?? [])?.filter(
          (filter): filter is NonNullable<typeof filter> => filter !== null,
        );

        logger?.debug(
          { auditId, formCount: forms.length, forms },
          'Loaded forms for sync',
        );

        // 2. Upsert audit + forms to backend → get server UUID
        const serializedAudit = AuditSchema.encode(audit);
        logger?.debug({ auditId }, 'Syncing audit to server');
        updateState({
          type: 'update',
          payload: {
            preparing: 'success',
          },
        });
        updateState({
          type: 'update_forms',
          payload: {
            mechanical: 'pending',
          },
        });
        const syncResult = await syncApiService.syncAudit({
          audit: serializedAudit,
          forms,
          accessToken,
        });

        logger?.debug(
          { auditId, serverAuditId: syncResult.id },
          'Audit synced successfully',
        );
        updateState({
          type: 'update_forms',
          payload: {
            mechanical: 'success',
          },
        });

        // 3. Upload attachment files using the server UUID
        for (const form of forms) {
          if (form.type !== 'mechanical') continue;

          updateState({
            type: 'update_attachments',
            payload: {
              mechanical: 'pending',
            },
          });
          const mechanicalForm = form as AuditMechanicalForm;
          for (const entry of mechanicalForm.entries) {
            if (!entry.attachments?.length) continue;

            for (const attachment of entry.attachments) {
              const fileId = attachment.name;

              const fileContent = await fileStorageService.load({ fileId });
              if (!fileContent) {
                logger?.warn(
                  { auditId, fileId },
                  'File not found in local storage, skipping upload',
                );
                continue;
              }

              logger?.debug(
                { auditId, serverAuditId: syncResult.id, fileId },
                'Getting file upload URL',
              );

              const uploadUrl = await syncApiService.getFileUploadUrl({
                serverAuditId: syncResult.id,
                fileId,
                accessToken,
                replace: true,
              });

              await syncApiService.uploadFileToUrl({
                url: uploadUrl,
                content: fileContent,
                mimeType: attachment.mimeType,
              });

              logger?.debug(
                { auditId, serverAuditId: syncResult.id, fileId },
                'File uploaded successfully',
              );
            }
          }

          updateState({
            type: 'update_attachments',
            payload: {
              mechanical: 'success',
            },
          });
        }

        return syncResult;
      },
    },
    queryClient,
  );
